import { useEffect, useMemo, useState } from 'react';
import type { UploadConfig } from '../types';
import { NumberedHeading } from './NumberedHeading';
import { UploadDropzone } from './UploadDropzone';
import { MultiUpload } from './MultiUpload';

export interface UploadFormData {
  files: File[];           // ordered: [humanFile?, petFile?] for humanpet; [petFile] for pet
  humanName: string;
  petName: string;
  petYears: string;        // optional, pet mode — e.g. "2010 - 2024"
  petQuote: string;        // optional, pet mode — memorial quote
  caption: string;         // optional, caption modes — "Family name / message"
  clothing: string;        // familypet-caption — chosen clothing-preference id
  charCount: number;       // 1 for pet-only, 2 for humanpet, 1..4 for default
}

interface Props {
  config: UploadConfig;
  disabled?: boolean;
  onSubmit: (data: UploadFormData) => void;
  styleSelected: boolean;
  /** Pre-written quotes from ui_config.pet_quotes (pet mode dropdown). */
  petQuotes?: string[];
  /** Selected style id — for dynamicPersonPetGroups modes this doubles as the
   *  person/pet-count combo (e.g. "2_persons_1_pet"). */
  styleId?: string | null;
}

/** Parses a "{N}_person(s)_{M}_pet(s)" style id into exact upload counts.
 *  Returns null when the id doesn't match (no combo chosen yet, or a
 *  non-combo style id on a mode that isn't dynamicPersonPetGroups). */
function parsePersonPetCombo(styleId: string | null | undefined): { person: number; pet: number } | null {
  if (!styleId) return null;
  const m = /^(\d+)_persons?_(\d+)_pets?$/.exec(styleId);
  if (!m) return null;
  return { person: Number(m[1]), pet: Number(m[2]) };
}

const FIELD_CLASS =
  'w-full min-w-0 px-4 py-3.5 rounded-[15px] bg-white border border-soft-border focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20 text-base text-warm-brown placeholder:text-warm-brown/40';
const LABEL_CLASS = 'text-[11px] font-bold tracking-[0.14em] uppercase text-warm-brown';
const COLUMN_LABEL_CLASS =
  'bf-pet-label text-[11px] font-bold tracking-[0.14em] uppercase text-warm-brown/80 mb-2';

function optionalTag() {
  return <span className="font-normal text-warm-brown/60">(optional)</span>;
}
function requiredTag() {
  return <span className="font-normal text-warm-brown/60">(required)</span>;
}

/**
 * UploadForm — Step 2 (upload) + Step 3 (pet details) of the landing flow.
 * Layout mirrors the WooCommerce source exactly, branched on input mode:
 *   pet / pet-angle    → single dropzone (Subject 1) + a numbered "Pet Details" step
 *   humanpet(-angle)   → two labelled columns (Your photo / Pet photo), each with
 *                        its own name input directly below the dropzone (no step 3)
 */
export function UploadForm({
  config,
  disabled,
  onSubmit,
  styleSelected,
  petQuotes = [],
  styleId = null,
}: Props) {
  const [humanFile, setHumanFile] = useState<File | null>(null);
  const [petFile, setPetFile] = useState<File | null>(null);
  const [humanName, setHumanName] = useState('');
  const [petName, setPetName] = useState('');
  const [petYears, setPetYears] = useState('');
  const [quote, setQuote] = useState(''); // free text — the dropdown just fills it
  const [caption, setCaption] = useState('');
  // familypet-caption: selected "Clothing Preference" id (defaults to recommended).
  const [clothing, setClothing] = useState(
    config.clothingDefaultId ?? config.clothingOptions?.[0]?.id ?? '',
  );
  // smallfamilypet-caption: files per upload group keyed by group.key.
  // (File | null)[] so dynamicPersonPetGroups can hold fixed, individually
  // addressable slots — an empty slot is `null`, not a removed array index.
  const [groupFiles, setGroupFiles] = useState<Record<string, (File | null)[]>>({});

  const groups = config.uploadGroups;
  const showBoth = config.showHumanUpload && config.showPetUpload;
  const groupFilesFlat = useMemo(
    () => (groups ?? []).flatMap((g) => (groupFiles[g.key] ?? []).filter((f): f is File => !!f)),
    [groups, groupFiles],
  );

  // dynamicPersonPetGroups (familypet-caption-45/-50): the chosen "style" id
  // IS the person/pet combo — resolve exact per-group counts from it, and
  // trim any already-added photos that no longer fit when the combo shrinks
  // (e.g. switching from "2 Persons" to "1 Person" after already adding 2).
  const combo = config.dynamicPersonPetGroups ? parsePersonPetCombo(styleId) : null;
  useEffect(() => {
    if (!combo) return;
    setGroupFiles((prev) => {
      const next = { ...prev };
      let changed = false;
      if ((next['adult']?.length ?? 0) > combo.person) {
        next['adult'] = (next['adult'] ?? []).slice(0, combo.person);
        changed = true;
      }
      if ((next['kidspet']?.length ?? 0) > combo.pet) {
        next['kidspet'] = (next['kidspet'] ?? []).slice(0, combo.pet);
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [combo]);

  const errors = useMemo(() => {
    const errs: Record<string, string> = {};
    if (groups) {
      if (config.dynamicPersonPetGroups) {
        if (!combo) {
          errs.files = 'Required';
        } else if (
          (groupFiles['adult'] ?? []).filter(Boolean).length !== combo.person
          || (groupFiles['kidspet'] ?? []).filter(Boolean).length !== combo.pet
        ) {
          errs.files = 'Required';
        }
      } else if (groupFilesFlat.length === 0) {
        errs.files = 'Required';
      }
    } else {
      if (config.showHumanUpload && !humanFile) errs.humanFile = 'Required';
      if (config.showPetUpload && !petFile) errs.petFile = 'Required';
      // Names are optional except Pet's name in pet mode.
      if (config.showPetName && config.petNameRequired && !petName.trim()) errs.petName = 'Required';
    }
    return errs;
  }, [config, groups, groupFilesFlat, groupFiles, combo, humanFile, petFile, petName]);

  const canSubmit = styleSelected && Object.keys(errors).length === 0 && !disabled;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSubmit) return;
    let files: File[] = [];
    if (groups) {
      files = groupFilesFlat;
    } else {
      if (humanFile) files.push(humanFile);
      if (petFile) files.push(petFile);
    }
    onSubmit({
      files,
      humanName: humanName.trim(),
      petName: petName.trim(),
      petYears: petYears.trim(),
      petQuote: quote.trim(),
      caption: caption.trim(),
      clothing: config.showClothing ? clothing : '',
      charCount: files.length || 1,
    });
  };

  // Number of upload subjects = number of dropzones shown (1 for pet/pet-angle).
  const subjectCount = config.maxSubjects;
  const subjectWord = subjectCount === 1 ? 'subject' : 'subjects';
  const uploadHeading = groups
    ? 'Upload Photos'
    : showBoth
      ? 'Upload Your Photo and Your Pet Photo'
      : `Upload photos (${subjectCount} ${subjectWord})`;

  const setGroup = (key: string, files: File[]) =>
    setGroupFiles((prev) => ({ ...prev, [key]: files }));

  // dynamicPersonPetGroups: each combo slot is its own fixed dropzone (not an
  // add/remove gallery) — set/replace the file at a specific index, padding
  // with null so earlier empty slots don't collapse.
  const setGroupSlot = (key: string, index: number, file: File | null) =>
    setGroupFiles((prev) => {
      const arr = [...(prev[key] ?? [])];
      while (arr.length <= index) arr.push(null);
      arr[index] = file;
      return { ...prev, [key]: arr };
    });

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-8">
      {/* ── Step 2 — Upload ─────────────────────────────────────────── */}
      <section className="flex flex-col gap-4">
        <NumberedHeading n={2}>{uploadHeading}</NumberedHeading>

        {groups && combo ? (
          // familypet-caption-45/-50, combo chosen — one fixed dropzone per
          // person/pet slot (matches template_human_pet_max_2_2.html: "Add
          // Person 1 photo" / "Add Person 2 photo" etc, not an add/remove gallery).
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <p className={COLUMN_LABEL_CLASS}>People photos</p>
              <div style={{ display: 'grid', gridTemplateColumns: combo.person > 1 ? 'minmax(0, 1fr) minmax(0, 1fr)' : 'minmax(0, 1fr)', gap: 12 }}>
                {Array.from({ length: combo.person }, (_, i) => (
                  <UploadDropzone
                    key={`adult-${i}`}
                    title={`Add Person ${i + 1} photo`}
                    lines={['Clear photo showing the face']}
                    file={groupFiles['adult']?.[i] ?? null}
                    onChange={(f) => setGroupSlot('adult', i, f)}
                    disabled={disabled}
                  />
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <p className={COLUMN_LABEL_CLASS}>Pets photos</p>
              <div style={{ display: 'grid', gridTemplateColumns: combo.pet > 1 ? 'minmax(0, 1fr) minmax(0, 1fr)' : 'minmax(0, 1fr)', gap: 12 }}>
                {Array.from({ length: combo.pet }, (_, i) => (
                  <UploadDropzone
                    key={`kidspet-${i}`}
                    title={`Add Pet ${i + 1} photo`}
                    lines={['Clear photo of your pet']}
                    file={groupFiles['kidspet']?.[i] ?? null}
                    onChange={(f) => setGroupSlot('kidspet', i, f)}
                    disabled={disabled}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : groups ? (
          // smallfamilypet-caption / familypet-caption — multi-image gallery
          // groups (Adult / Kids & Pets). Also the fallback for
          // familypet-caption-45/-50 before a combo is chosen in Step 1.
          <div className="flex flex-col gap-6">
            {groups.map((g) => (
              <MultiUpload
                key={g.key}
                label={g.label}
                subLabel={g.subLabel}
                max={g.max}
                emptyTitle={g.emptyTitle}
                emptyHint={g.emptyHint}
                files={(groupFiles[g.key] ?? []).filter((f): f is File => !!f)}
                onChange={(f) => setGroup(g.key, f)}
                disabled={disabled}
              />
            ))}
          </div>
        ) : showBoth ? (
          // humanpet / humanpet-angle — two labelled columns (50/50), name input below each slot.
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 12 }}>
            <div className="flex flex-col min-w-0">
              <p className={COLUMN_LABEL_CLASS}>Your photo</p>
              <UploadDropzone
                title="Upload Your Photo"
                file={humanFile}
                onChange={setHumanFile}
                disabled={disabled}
              />
              {config.showHumanName && (
                <label className="flex flex-col gap-2 mt-3">
                  <span className={`bf-name-label ${LABEL_CLASS}`}>Your name {optionalTag()}</span>
                  <input
                    type="text"
                    value={humanName}
                    onChange={(e) => setHumanName(e.target.value)}
                    disabled={disabled}
                    maxLength={80}
                    className={FIELD_CLASS}
                    placeholder="e.g. Emma, Dad, Nana…"
                  />
                </label>
              )}
            </div>
            <div className="flex flex-col min-w-0">
              <p className={COLUMN_LABEL_CLASS}>Pet photo</p>
              <UploadDropzone
                title="Upload Pet Photo"
                file={petFile}
                onChange={setPetFile}
                disabled={disabled}
              />
              {config.showPetName && (
                <label className="flex flex-col gap-2 mt-3">
                  <span className={`bf-name-label ${LABEL_CLASS}`}>Pet name {optionalTag()}</span>
                  <input
                    type="text"
                    value={petName}
                    onChange={(e) => setPetName(e.target.value)}
                    disabled={disabled}
                    maxLength={80}
                    className={FIELD_CLASS}
                    placeholder="e.g. Buddy, Luna, Bella…"
                  />
                </label>
              )}
            </div>
          </div>
        ) : (
          // pet / pet-angle — single dropzone for one subject.
          <UploadDropzone
            title="Upload Photo"
            lines={['Subject 1']}
            file={petFile}
            onChange={setPetFile}
            disabled={disabled}
          />
        )}
      </section>

      {/* ── Clothing Preference (familypet-caption) — above the caption ─ */}
      {config.showClothing && (config.clothingOptions?.length ?? 0) > 0 && (
        <section className="flex flex-col gap-4">
          <NumberedHeading n={3}>{config.clothingLabel ?? 'Clothing Preference'}</NumberedHeading>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16 }}>
            {config.clothingOptions!.map((opt) => {
              const selected = clothing === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setClothing(opt.id)}
                  disabled={disabled}
                  aria-pressed={selected}
                  className={[
                    'relative flex flex-col items-center text-center gap-2 rounded-[15px] bg-white px-5 pt-8 pb-6 shadow-soft transition-colors',
                    selected ? 'border-2 border-teal' : 'border-2 border-transparent hover:border-soft-border',
                  ].join(' ')}
                >
                  {opt.recommended && (
                    <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-teal px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.05em] text-white">
                      Recommended
                    </span>
                  )}
                  {selected && (
                    <span className="absolute top-3 right-3 flex h-[22px] w-[22px] items-center justify-center rounded-full bg-teal text-xs text-white">
                      ✓
                    </span>
                  )}
                  <span className="text-[28px] leading-none text-teal" aria-hidden="true">{opt.icon}</span>
                  <span className="text-[15px] font-semibold text-warm-brown">{opt.title}</span>
                  <span className="text-xs leading-relaxed text-warm-brown/60">{opt.sub}</span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Caption (humanpet-caption / smallfamilypet-caption) ─────── */}
      {config.showCaption && (
        <label className="flex flex-col gap-2">
          <span className={LABEL_CLASS}>
            {config.captionLabel ?? 'Family name / message'} {optionalTag()}
            <span className="ml-2 font-normal normal-case tracking-normal text-warm-brown/55">
              {caption.length}/{config.captionMaxLen ?? 25}
            </span>
          </span>
          <input
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            disabled={disabled}
            maxLength={config.captionMaxLen ?? 25}
            className={FIELD_CLASS}
            placeholder="e.g. Max Family, Lily & Rose…"
          />
        </label>
      )}

      {/* ── Step 3 — Pet Details (pet / pet-angle only) ─────────────── */}
      {!showBoth && config.showPetName && (
        <section className="flex flex-col gap-4">
          <NumberedHeading n={3}>Pet Details</NumberedHeading>

          <label className="flex flex-col gap-2">
            <span className={LABEL_CLASS}>
              Pet name {config.petNameRequired ? requiredTag() : optionalTag()}
            </span>
            <input
              type="text"
              value={petName}
              onChange={(e) => setPetName(e.target.value)}
              disabled={disabled}
              maxLength={config.petNameMaxLen ?? 80}
              className={FIELD_CLASS}
              placeholder="Enter pet's name"
            />
          </label>

          {config.showPetYears && (
            <label className="flex flex-col gap-2">
              <span className={LABEL_CLASS}>Years {optionalTag()}</span>
              <input
                type="text"
                value={petYears}
                onChange={(e) => setPetYears(e.target.value)}
                disabled={disabled}
                maxLength={40}
                className={FIELD_CLASS}
                placeholder="e.g. 2010 - 2024"
              />
            </label>
          )}

          {config.showPetQuote && (
            <label className="flex flex-col gap-2">
              <span className={LABEL_CLASS}>Choose a quote {optionalTag()}</span>
              {petQuotes.length > 0 && (
                <select
                  value=""
                  onChange={(e) => { if (e.target.value) setQuote(e.target.value); }}
                  disabled={disabled}
                  className={FIELD_CLASS + ' appearance-none cursor-pointer'}
                >
                  <option value="">Select a pre-written quote…</option>
                  {petQuotes.map((q) => (
                    <option key={q} value={q}>{q}</option>
                  ))}
                </select>
              )}
              <textarea
                value={quote}
                onChange={(e) => setQuote(e.target.value)}
                disabled={disabled}
                maxLength={120}
                rows={3}
                className={FIELD_CLASS + ' resize-none'}
                placeholder="Or write your own quote…"
              />
            </label>
          )}
        </section>
      )}

      {/* Generate button */}
      <button
        type="submit"
        disabled={!canSubmit}
        className={[
          'w-full inline-flex items-center justify-center gap-2.5 py-4 rounded-[10px] font-semibold text-base transition-colors',
          canSubmit
            ? 'bg-teal text-white shadow-soft hover:bg-teal-dark'
            : 'bg-teal/30 text-white/85 cursor-not-allowed',
        ].join(' ')}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2l2.6 6.6 7 .5-5.4 4.5 1.7 6.8L12 16.8l-5.9 3.6 1.7-6.8L2.4 9.1l7-.5L12 2z" />
        </svg>
        Generate My Portrait
      </button>

      {!styleSelected && (
        <p className="text-center text-xs text-warm-brown/70 -mt-2">Pick a style above to continue.</p>
      )}
    </form>
  );
}
